// Throwable equipment and the defusal bomb. Models are small (real-world size, a touch chunky) and
// share geometry and materials through a module cache: `createEquipmentModel` is cheap enough to call
// per projectile. Local space: origin at the centre of mass, the knife points along -Z.

import * as THREE from "three";
import { MeshBuilder } from "./geom";
import { ACCENT, ARCH, CHAR, GUN, shade } from "../materials/palette";

export type EquipmentName = "frag" | "throwing_knife" | "flash" | "smoke" | "impulse";

export const EQUIPMENT_IDS: readonly EquipmentName[] = ["frag", "throwing_knife", "flash", "smoke", "impulse"];

type MatKey = "main" | "metal" | "emit";

const OLIVE = "#4f5d3f";
const FLASH_BODY = "#d9d7d2";
const SMOKE_BODY = "#6f7782";

function buildFrag(b: MeshBuilder): void {
  b.lathe(
    "main",
    [
      [0, -0.046],
      [0.024, -0.042],
      [0.036, -0.022],
      [0.038, 0.008],
      [0.031, 0.034],
      [0.016, 0.046],
      [0, 0.046],
    ],
    8,
    [0, 0, 0],
    { color: OLIVE },
  );
  // Raised grooves give the frag its segmented look
  for (const y of [-0.016, 0.006, 0.026]) {
    b.cyl("main", "y", 0.006, 0.0385, 0.0385, [0, y, 0], { segments: 8, color: shade(OLIVE, 0.7) });
  }
  b.cyl("metal", "y", 0.022, 0.012, 0.012, [0, 0.056, 0], { segments: 6, color: GUN.steel });
  b.box("metal", [0.008, 0.07, 0.016], [0.036, 0.012, 0], { color: GUN.steel });
  b.lathe(
    "main",
    [
      [0.007, -0.0015],
      [0.011, -0.0015],
      [0.011, 0.0015],
      [0.007, 0.0015],
    ],
    8,
    [0.016, 0.07, 0],
    { axis: "z", color: ACCENT.runnerRed },
  );
}

function buildThrowingKnife(b: MeshBuilder): void {
  b.prism(
    "metal",
    [[-0.02, 0.014], [-0.125, 0.014], [-0.175, 0], [-0.125, -0.014], [-0.02, -0.014]],
    0.004,
    [0, 0, 0],
    { color: GUN.steel },
  );
  b.box("main", [0.02, 0.028, 0.075], [0, 0, 0.0175], { bevel: 0.004, color: CHAR.gloves });
  for (const z of [0.0, 0.02, 0.04]) b.box("main", [0.022, 0.03, 0.007], [0, 0, z], { color: ACCENT.runnerRed });
  b.box("metal", [0.026, 0.034, 0.01], [0, 0, 0.058], { bevel: 0.003, color: GUN.steelDark });
}

function buildCanister(b: MeshBuilder, body: string, band: string, radius: number, length: number): void {
  const h = length / 2;
  b.cyl("main", "y", length, radius, radius, [0, 0, 0], { segments: 10, color: body });
  b.cyl("main", "y", 0.018, radius * 1.02, radius * 1.02, [0, h * 0.35, 0], { segments: 10, color: band });
  b.cyl("metal", "y", 0.012, radius * 0.8, radius * 0.8, [0, h + 0.006, 0], { segments: 10, color: GUN.steel });
  b.cyl("metal", "y", 0.012, radius * 0.85, radius * 0.85, [0, -h - 0.006, 0], { segments: 10, color: GUN.steel });
  b.box("metal", [0.008, length * 0.85, 0.016], [radius + 0.003, 0.004, 0], { color: GUN.steel });
  b.cyl("metal", "y", 0.012, 0.01, 0.01, [0, h + 0.018, 0], { segments: 6, color: GUN.steel });
}

function buildFlash(b: MeshBuilder): void {
  buildCanister(b, FLASH_BODY, ACCENT.safetyYellow, 0.028, 0.1);
  for (const a of [0, 1, 2, 3]) {
    const t = (a * Math.PI) / 2;
    b.box("main", [0.004, 0.02, 0.012], [Math.cos(t) * 0.0285, -0.015, Math.sin(t) * 0.0285], {
      rot: [0, -t, 0],
      color: CHAR.strap,
    });
  }
}

function buildSmoke(b: MeshBuilder): void {
  buildCanister(b, SMOKE_BODY, ARCH.offWhite, 0.032, 0.115);
  b.box("main", [0.02, 0.008, 0.006], [0, 0.0575, 0.012], { color: CHAR.strap });
  b.box("main", [0.02, 0.008, 0.006], [0, 0.0575, -0.012], { color: CHAR.strap });
}

function buildImpulse(b: MeshBuilder): void {
  b.sphere("main", 0.04, [0, 0, 0], 10, 6, { color: ACCENT.deepBlue });
  b.lathe(
    "emit",
    [
      [0.039, -0.007],
      [0.045, -0.007],
      [0.045, 0.007],
      [0.039, 0.007],
    ],
    10,
    [0, 0, 0],
    { color: ACCENT.teal },
  );
  b.cyl("metal", "y", 0.02, 0.012, 0.012, [0, 0.048, 0], { segments: 6, color: GUN.steel });
  b.box("metal", [0.008, 0.06, 0.016], [0.038, 0.012, 0], { color: GUN.steel });
}

const BUILDERS: Readonly<Record<EquipmentName, (b: MeshBuilder) => void>> = {
  frag: buildFrag,
  throwing_knife: buildThrowingKnife,
  flash: buildFlash,
  smoke: buildSmoke,
  impulse: buildImpulse,
};

// Shared resources
const geometryCache = new Map<string, Record<string, THREE.BufferGeometry>>();
let sharedMaterials: Record<MatKey, THREE.MeshStandardMaterial> | null = null;

function materials(): Record<MatKey, THREE.MeshStandardMaterial> {
  if (sharedMaterials === null) {
    sharedMaterials = {
      main: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.72, metalness: 0.1 }),
      metal: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.4, metalness: 0.85 }),
      emit: new THREE.MeshStandardMaterial({
        vertexColors: true,
        color: 0x101010,
        emissive: new THREE.Color(ACCENT.teal),
        emissiveIntensity: 1.6,
        roughness: 0.5,
      }),
    };
  }
  return sharedMaterials;
}

/**
 * A fresh Group for a projectile / held equipment item. Geometry and materials are shared and
 * owned by this module: do not dispose them (see disposeEquipmentCache). Throws for unknown ids.
 */
export function createEquipmentModel(id: string): THREE.Group {
  const builder = BUILDERS[id as EquipmentName] as ((b: MeshBuilder) => void) | undefined;
  if (builder === undefined) throw new Error(`unknown equipment "${id}"`);
  let geos = geometryCache.get(id);
  if (geos === undefined) {
    const b = new MeshBuilder();
    builder(b);
    geos = b.build({ vertexColors: true });
    geometryCache.set(id, geos);
  }
  const mats = materials();
  const group = new THREE.Group();
  group.name = `equipment:${id}`;
  for (const [key, geo] of Object.entries(geos)) {
    const mesh = new THREE.Mesh(geo, mats[key as MatKey]);
    mesh.name = `${id}:${key}`;
    group.add(mesh);
  }
  return group;
}

/** Frees the shared equipment geometry and materials (all models must be out of use). */
export function disposeEquipmentCache(): void {
  for (const set of geometryCache.values()) for (const g of Object.values(set)) g.dispose();
  geometryCache.clear();
  if (sharedMaterials !== null) {
    for (const m of Object.values(sharedMaterials)) m.dispose();
    sharedMaterials = null;
  }
}

// ---------------------------------------------------------------------------------------------
// Defusal bomb

export type BombState = "idle" | "planted" | "defused";

export interface BombModel {
  root: THREE.Group;
  /**
   * idle: dim amber panel; planted: red panel blinking from 1 Hz to 6 Hz as `fraction` (elapsed / fuse,
   * 0..1) approaches 1; defused: steady green. `dt` is the seconds since the previous call.
   */
  update(state: BombState, dt: number, fraction?: number): void;
  dispose(): void;
}

/** Defusal bomb: a heavy dark brick with straps, wires and an emissive display panel. 0.34 x 0.11 x 0.22 m. */
export function createBombModel(): BombModel {
  const b = new MeshBuilder();
  b.box("main", [0.34, 0.11, 0.22], [0, 0.055, 0], { bevel: 0.012, color: ARCH.darkTrim });
  b.box("main", [0.3, 0.012, 0.18], [0, 0.116, 0], { bevel: 0.003, color: CHAR.strap });
  b.box("main", [0.02, 0.114, 0.224], [-0.1, 0.057, 0], { color: ACCENT.runnerRed });
  b.box("main", [0.02, 0.114, 0.224], [0.1, 0.057, 0], { color: ACCENT.runnerRed });
  b.box("metal", [0.012, 0.012, 0.012], [-0.16, 0.12, 0.08], { color: GUN.steel });
  b.cyl("metal", "y", 0.12, 0.004, 0.004, [-0.16, 0.18, 0.08], { segments: 5, color: GUN.steel });
  b.cyl("main", "x", 0.12, 0.006, 0.006, [0.0, 0.126, -0.075], { segments: 5, color: ACCENT.safetyYellow });
  b.cyl("main", "x", 0.12, 0.006, 0.006, [0.0, 0.126, -0.06], { segments: 5, color: ACCENT.deepBlue });
  for (const x of [-0.14, 0.14]) {
    for (const z of [-0.08, 0.08]) b.box("metal", [0.03, 0.01, 0.03], [x, 0.0, z], { color: GUN.steelDark });
  }
  const panelBuilder = new MeshBuilder();
  panelBuilder.box("panel", [0.15, 0.006, 0.07], [0, 0.125, 0.03]);
  for (let i = 0; i < 4; i++) {
    panelBuilder.box("panel", [0.026, 0.008, 0.044], [-0.06 + i * 0.04, 0.127, 0.03]);
  }
  panelBuilder.box("panel", [0.012, 0.012, 0.012], [0.11, 0.124, 0.075]);

  const body = b.build({ vertexColors: true });
  const panelGeo = panelBuilder.build();
  const mats = {
    main: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.75, metalness: 0.15 }),
    metal: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.4, metalness: 0.85 }),
  };
  const panel = new THREE.MeshStandardMaterial({
    color: 0x080808,
    emissive: new THREE.Color(ACCENT.signalOrange),
    emissiveIntensity: 0.4,
    roughness: 0.4,
  });

  const root = new THREE.Group();
  root.name = "bomb";
  const meshes: THREE.Mesh[] = [];
  for (const [key, geo] of Object.entries(body)) {
    const mesh = new THREE.Mesh(geo, key === "metal" ? mats.metal : mats.main);
    mesh.castShadow = true;
    meshes.push(mesh);
    root.add(mesh);
  }
  for (const geo of Object.values(panelGeo)) {
    const mesh = new THREE.Mesh(geo, panel);
    meshes.push(mesh);
    root.add(mesh);
  }

  const amber = new THREE.Color(ACCENT.signalOrange);
  const red = new THREE.Color(ACCENT.runnerRed);
  const green = new THREE.Color("#3ddc84");

  /** Blink phase in cycles: accumulated, because the rate changes and `time * rate` would jump with it. */
  let blink = 0;

  return {
    root,
    update(state: BombState, dt: number, fraction = 0): void {
      if (state === "planted") {
        const hz = 1 + 5 * Math.min(Math.max(fraction, 0), 1);
        blink = (blink + dt * hz) % 1;
        const on = blink < 0.5;
        panel.emissive.copy(red);
        panel.emissiveIntensity = on ? 2.2 : 0.35;
      } else if (state === "defused") {
        panel.emissive.copy(green);
        panel.emissiveIntensity = 1.4;
      } else {
        panel.emissive.copy(amber);
        panel.emissiveIntensity = 0.5;
      }
    },
    dispose(): void {
      root.removeFromParent();
      for (const m of meshes) m.geometry.dispose();
      mats.main.dispose();
      mats.metal.dispose();
      panel.dispose();
    },
  };
}
